// Money is always a string end-to-end. Never use `number` for money.
export type Money = string;

// Number of decimal places used when displaying money/cost amounts. The
// default is 2; the active company's `decimal_places` setting overrides it at
// session load via setMoneyFractionDigits(), so every formatMoney() call site
// picks up the configured precision without per-call changes.
let defaultFractionDigits = 2;

export function setMoneyFractionDigits(n: number | null | undefined): void {
  const v = Math.round(Number(n));
  if (Number.isFinite(v) && v >= 0 && v <= 6) defaultFractionDigits = v;
}

export function getMoneyFractionDigits(): number {
  return defaultFractionDigits;
}

// Formatting locale. It decides which currency gets the BARE symbol and which
// gets disambiguated: under en-US, CAD prints "CA$4,218" and USD prints
// "$4,218"; under en-CA it is the other way round ("$4,218" / "US$4,218") —
// the Canadian convention. So the locale must follow the COMPANY, not the
// developer: region comes from the functional currency, language from the
// company's language setting (setMoneyLocale at session load).
const CURRENCY_REGION: Record<string, string> = {
  USD: 'US', CAD: 'CA', GBP: 'GB', AUD: 'AU', NZD: 'NZ', JPY: 'JP',
  CHF: 'CH', CNY: 'CN', MXN: 'MX', ILS: 'IL', INR: 'IN', BRL: 'BR', ZAR: 'ZA',
};
// The euro spans many regions — pick the one matching the UI language.
const EUR_REGION: Record<string, string> = { de: 'DE', fr: 'FR', es: 'ES', pt: 'PT', it: 'IT', nl: 'NL' };
let defaultLocale = 'en-US';

export function setMoneyLocale(functionalCurrency?: string | null, language?: string | null): void {
  const lang = (language || 'en').slice(0, 2).toLowerCase();
  const ccy = (functionalCurrency || '').toUpperCase();
  const region = ccy === 'EUR' ? (EUR_REGION[lang] || 'IE') : CURRENCY_REGION[ccy];
  // DIGITS ARE FIXED app-wide: comma thousands, dot decimals (user rule
  // 2026-08-15 — a French/European company language must not flip amounts to
  // "4 218,30 $"). The region still follows the functional currency so the
  // home currency gets the bare symbol and foreign ones are disambiguated;
  // only the language part is pinned to English formatting conventions.
  defaultLocale = region ? `en-${region}` : 'en';
}

export function getMoneyLocale(): string {
  return defaultLocale;
}

export function formatMoney(
  value: Money | number | null | undefined,
  currency = 'USD',
  locale = defaultLocale,
  places?: number,
): string {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  let digits = places == null ? defaultFractionDigits : places;
  // The company decimal_places setting is a DISPLAY preference, not licence to
  // lie: when it would round away real cents (1,158.50 → "1,159"), escalate to
  // the currency's standard 2 places so the shown amount stays exact. Callers
  // that pass `places` explicitly (deliberately rounded KPIs) are untouched.
  if (places == null && digits < 2) {
    const factor = 10 ** digits;
    if (Math.round(n * factor) / factor !== n) digits = 2;
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
}

export function parseMoney(input: string): Money {
  // Strip any currency symbol / whitespace / thousand separators.
  const cleaned = input.replace(/[^\d.-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return '0.0000';
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return '0.0000';
  return n.toFixed(4);
}

// Drop trailing zeros and a dangling decimal point for display. The
// canonical storage form is still 4 decimals (parseMoney above); this is
// purely what the user sees in an <input>. Examples:
//   "1.0000"   -> "1"
//   "1.5000"   -> "1.5"
//   "0.1700"   -> "0.17"
//   "1.0001"   -> "1.0001"
//   "0.0000"   -> "0"
export function trimTrailingZeros(input: string | null | undefined): string {
  const s = input == null ? '' : String(input);
  if (!s || !s.includes('.')) return s;
  const trimmed = s.replace(/\.?0+$/, '');
  return trimmed === '' || trimmed === '-' ? '0' : trimmed;
}
